/**
 * @fileoverview Application entry point.
 * @package PlayItNow
 * @module apps/front/main
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App.tsx'
import './styles/index.css'
import { initTheme } from './lib/theme-init'
import { analyticsEnabled } from './config'

// CSP-safe: non-blocking CSS activation for link[media="print"] (no inline scripts/handlers).
;(function () {
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][media="print"]')
  links.forEach((link) => {
    link.addEventListener('load', () => { link.media = 'all' })
    if (link.sheet) link.media = 'all'
  })
})()

// Initialize theme on app load (before React renders)
initTheme()

// Rybbit analytics: inject script only when enabled (prod only, not preprod)
if (analyticsEnabled && typeof document !== 'undefined') {
  const script = document.createElement('script')
  script.src = 'https://analytics.axialdata.app/api/script.js'
  script.setAttribute('data-site-id', '889582baabb0')
  script.defer = true
  document.head.appendChild(script)
}

// Capture beforeinstallprompt event early (before React mounts)
// Store it in a global variable that the usePWA hook can access
declare global {
  interface Window {
    __deferredInstallPrompt?: Event & {
      prompt: () => Promise<void>
      userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
    }
  }
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  // Store the event globally so it can be accessed by usePWA hook
  window.__deferredInstallPrompt = e as Window['__deferredInstallPrompt']
  // Also store in sessionStorage to persist across reloads
  sessionStorage.setItem('playitnow_pwa_installable', 'true')
  if (import.meta.env.DEV) {
    console.log('PWA install prompt captured early')
  }
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
